// src/utils/payloadAnalyzer.js
// Utility to analyze share payload size and identify what's making it large

/**
 * Analyze a payload and identify size contributors
 */
export function analyzePayload(payload) {
  if (!payload || typeof payload !== 'object') {
    return { error: 'Invalid payload' };
  }

  const json = JSON.stringify(payload);
  const totalBytes = new TextEncoder().encode(json).byteLength;
  const totalKB = (totalBytes / 1024).toFixed(2);

  const analysis = {
    total: {
      bytes: totalBytes,
      kb: totalKB,
      mb: (totalBytes / 1024 / 1024).toFixed(2)
    },
    structure: {
      pages: payload.pages?.length || 0,
      photos: payload.photos?.length || 0
    },
    breakdown: {},
    warnings: [],
    recommendations: []
  };

  // Analyze each top-level key
  for (const [key, value] of Object.entries(payload)) {
    const keyJson = JSON.stringify(value);
    const keyBytes = new TextEncoder().encode(keyJson).byteLength;
    const keyKB = (keyBytes / 1024).toFixed(2);
    
    analysis.breakdown[key] = {
      bytes: keyBytes,
      kb: keyKB,
      percentage: ((keyBytes / totalBytes) * 100).toFixed(1) + '%'
    };
  }

  // Check for data URLs in photos
  let dataUrlCount = 0;
  let dataUrlTotalSize = 0;
  
  if (payload.photos && Array.isArray(payload.photos)) {
    payload.photos.forEach((photo, idx) => {
      const photoSrc = photo.src || photo.url || '';
      if (photoSrc.startsWith('data:')) {
        dataUrlCount++;
        dataUrlTotalSize += photoSrc.length;
        
        if (idx < 3) { // Show first 3 examples
          analysis.warnings.push({
            type: 'data-url-photo',
            location: `photos[${idx}]`,
            id: photo.id,
            size: (photoSrc.length / 1024).toFixed(2) + ' KB',
            preview: photoSrc.substring(0, 50) + '...'
          });
        }
      }
    });
  }

  // Check pages for data URLs
  if (payload.pages && Array.isArray(payload.pages)) {
    payload.pages.forEach((page, pageIdx) => {
      // Check page.photos
      if (page.photos && Array.isArray(page.photos)) {
        page.photos.forEach((photo, photoIdx) => {
          const photoSrc = photo.src || photo.url || '';
          if (photoSrc.startsWith('data:')) {
            dataUrlCount++;
            dataUrlTotalSize += photoSrc.length;
            
            if (dataUrlCount <= 3) {
              analysis.warnings.push({
                type: 'data-url-page-photo',
                location: `pages[${pageIdx}].photos[${photoIdx}]`,
                id: photo.id,
                size: (photoSrc.length / 1024).toFixed(2) + ' KB',
                preview: photoSrc.substring(0, 50) + '...'
              });
            }
          }
        });
      }
      
      // Check page.items
      if (page.items && Array.isArray(page.items)) {
        page.items.forEach((item, itemIdx) => {
          const itemUrl = item.url || item.src || '';
          if (itemUrl.startsWith('data:')) {
            dataUrlCount++;
            dataUrlTotalSize += itemUrl.length;
            
            if (dataUrlCount <= 3) {
              analysis.warnings.push({
                type: 'data-url-page-item',
                location: `pages[${pageIdx}].items[${itemIdx}]`,
                id: item.id || item.photoId,
                size: (itemUrl.length / 1024).toFixed(2) + ' KB',
                preview: itemUrl.substring(0, 50) + '...'
              });
            }
          }
        });
      }
    });
  }

  // Add summary if data URLs found
  if (dataUrlCount > 0) {
    const dataUrlMB = (dataUrlTotalSize / 1024 / 1024).toFixed(2);
    analysis.dataUrls = {
      count: dataUrlCount,
      totalSize: dataUrlTotalSize,
      totalKB: (dataUrlTotalSize / 1024).toFixed(2),
      totalMB: dataUrlMB,
      percentage: ((dataUrlTotalSize / totalBytes) * 100).toFixed(1) + '%'
    };

    analysis.recommendations.push({
      priority: 'CRITICAL',
      issue: `Found ${dataUrlCount} photos with base64 data URLs`,
      impact: `Using ${dataUrlMB} MB (${analysis.dataUrls.percentage} of total payload)`,
      fix: 'Upload photos to Firebase Storage first, then use storage URLs instead of data URLs'
    });
  }

  // Size-based recommendations
  if (totalBytes > 400 * 1024) {
    analysis.recommendations.push({
      priority: 'HIGH',
      issue: `Payload is ${totalKB} KB (exceeds 400 KB inline limit)`,
      impact: 'Will require Firebase Storage upload for sharing',
      fix: 'This is OK if Firebase is configured correctly, but reduce payload size if possible'
    });
  }

  if (totalBytes > 2000) {
    const urlLength = json.length * 1.4; // Approximate URL-encoded length
    analysis.urlLength = {
      estimated: Math.floor(urlLength),
      displayLength: (urlLength / 1024).toFixed(2) + ' KB'
    };
  }

  return analysis;
}

/**
 * Print analysis to console in a readable format
 */
export function printAnalysis(analysis) {
  console.log('\n' + '='.repeat(70));
  console.log('📊 PAYLOAD SIZE ANALYSIS');
  console.log('='.repeat(70));
  
  console.log('\n📦 Total Size:');
  console.log(`   ${analysis.total.bytes.toLocaleString()} bytes`);
  console.log(`   ${analysis.total.kb} KB`);
  console.log(`   ${analysis.total.mb} MB`);
  
  console.log('\n📋 Structure:');
  console.log(`   Pages: ${analysis.structure.pages}`);
  console.log(`   Photos: ${analysis.structure.photos}`);
  
  console.log('\n🔍 Size Breakdown by Key:');
  for (const [key, data] of Object.entries(analysis.breakdown)) {
    console.log(`   ${key}: ${data.kb} KB (${data.percentage})`);
  }
  
  if (analysis.dataUrls) {
    console.log('\n⚠️  DATA URL WARNING:');
    console.log(`   Found: ${analysis.dataUrls.count} photos with base64 data`);
    console.log(`   Total: ${analysis.dataUrls.totalMB} MB (${analysis.dataUrls.percentage})`);
    console.log(`   This is likely the cause of your large payload!`);
  }
  
  if (analysis.warnings.length > 0) {
    console.log('\n⚠️  Examples of Data URLs Found:');
    analysis.warnings.slice(0, 5).forEach((warning, idx) => {
      console.log(`   ${idx + 1}. ${warning.location}: ${warning.size}`);
      console.log(`      ${warning.preview}`);
    });
    if (analysis.warnings.length > 5) {
      console.log(`   ... and ${analysis.warnings.length - 5} more`);
    }
  }
  
  if (analysis.recommendations.length > 0) {
    console.log('\n💡 RECOMMENDATIONS:');
    analysis.recommendations.forEach((rec, idx) => {
      console.log(`\n   ${idx + 1}. [${rec.priority}] ${rec.issue}`);
      console.log(`      Impact: ${rec.impact}`);
      console.log(`      Fix: ${rec.fix}`);
    });
  }
  
  console.log('\n' + '='.repeat(70) + '\n');
  
  return analysis;
}

/**
 * Quick helper to analyze and print in one call
 */
export function analyzeAndPrint(payload) {
  const analysis = analyzePayload(payload);
  return printAnalysis(analysis);
}

/**
 * Estimate optimal payload (without data URLs)
 */
export function estimateOptimalSize(payload) {
  if (!payload) return null;
  
  // Create a copy without data URLs
  const optimized = JSON.parse(JSON.stringify(payload));
  
  const replaceDataUrls = (obj) => {
    if (!obj || typeof obj !== 'object') return;
    
    if (Array.isArray(obj)) {
      obj.forEach(replaceDataUrls);
      return;
    }
    
    for (const [key, value] of Object.entries(obj)) {
      if ((key === 'src' || key === 'url') && typeof value === 'string' && value.startsWith('data:')) {
        obj[key] = 'https://storage.googleapis.com/bucket/photo-id.jpg'; // Typical URL
      } else if (typeof value === 'object') {
        replaceDataUrls(value);
      }
    }
  };
  
  replaceDataUrls(optimized);
  
  const optimalJson = JSON.stringify(optimized);
  const optimalBytes = new TextEncoder().encode(optimalJson).byteLength;
  const optimalKB = (optimalBytes / 1024).toFixed(2);
  
  const originalBytes = new TextEncoder().encode(JSON.stringify(payload)).byteLength;
  const reduction = originalBytes - optimalBytes;
  const reductionPercent = ((reduction / originalBytes) * 100).toFixed(1);
  
  return {
    original: {
      bytes: originalBytes,
      kb: (originalBytes / 1024).toFixed(2)
    },
    optimal: {
      bytes: optimalBytes,
      kb: optimalKB
    },
    reduction: {
      bytes: reduction,
      kb: (reduction / 1024).toFixed(2),
      percentage: reductionPercent + '%'
    }
  };
}
